import React from 'react';
import { injectIntl } from 'react-intl';
import { Grid, Typography } from '@mui/material';
import _ from 'lodash';
import { THEME_COLOR_PALETTES, getValidThemeColorOption } from '../../../constants/my-details';
import AdviserInfoTagChip from '../AdviserInfoTagChip/AdviserInfoTagChip';
import { getHobbyIntl, getMainLanguageIntl } from '../../../utils/my-details';
import { tryTranslate } from '../../../utils/locale';
import { tagsToTagsString } from '../../../utils/user';
import AdviserInfoCardAbstract from '../AdviserInfoCardAbstract/AdviserInfoCardAbstract';

const FullWidthStyle = {
  width: '100%'
};

const AdviserInfoPageSocial = props => {
  const { intl, adviserInfo, isIntoView } = props;

  const { mainLanguages, hobbies, customizedHobby, university, secondarySchool, familiarPlaces, otherHashtags } =
    adviserInfo || {};
  const themeColor = getValidThemeColorOption(adviserInfo);
  const borderColor = THEME_COLOR_PALETTES[themeColor][1];
  const formattedMainLanguages = mainLanguages || [];
  const formattedHobbies = hobbies || [];
  const formattedUniversity = university || [];
  const formattedSecondarySchool = secondarySchool || [];
  const schools = [...formattedUniversity, ...formattedSecondarySchool];
  const formattedFamiliarPlaces = familiarPlaces || [];

  return (
    <AdviserInfoCardAbstract
      className="adviser-info-page-social"
      title={intl.formatMessage({ id: 'adv-info-social-title' })}
      adviserInfo={adviserInfo}
      contentHeight="calc(100% - 110px)"
      isIntoView={isIntoView}
    >
      <Grid container wrap="nowrap" sx={{ flexDirection: 'column' }}>
        <Grid
          style={{
            ...FullWidthStyle,
            flexGrow: 1,
            paddingBottom: 34
          }}
          className="content-container"
        >
          <Grid container spacing={3} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <Grid style={FullWidthStyle}>
              <Grid
                container
                spacing={1}
                sx={{ flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}
              >
                <Grid>
                  <Typography
                    className="emoji"
                    sx={{
                      display: 'inline'
                    }}
                  >
                    ⚽
                  </Typography>
                  <Typography
                    className="title"
                    sx={{
                      display: 'inline'
                    }}
                  >
                    {intl.formatMessage({ id: 'hobbies' })}
                  </Typography>
                </Grid>
                <Grid>
                  {formattedHobbies.length > 0 ? (
                    <Grid
                      container
                      spacing={1}
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'flex-start'
                      }}
                    >
                      {formattedHobbies.map(tag => (
                        <Grid key={tag}>
                          <AdviserInfoTagChip
                            color={borderColor}
                            label={tryTranslate(intl, getHobbyIntl(tag))}
                            isIntoView={isIntoView}
                          />
                        </Grid>
                      ))}
                      {!!customizedHobby && (
                        <Grid>
                          <AdviserInfoTagChip color={borderColor} label={customizedHobby} isIntoView={isIntoView} />
                        </Grid>
                      )}
                    </Grid>
                  ) : (
                    <Typography color="textSecondary">-</Typography>
                  )}
                </Grid>
              </Grid>
            </Grid>

            <Grid style={FullWidthStyle}>
              <Grid container spacing={1} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <Grid>
                  <Typography
                    className="emoji"
                    sx={{
                      display: 'inline'
                    }}
                  >
                    ✈️
                  </Typography>
                  <Typography
                    sx={{
                      display: 'inline'
                    }}
                  >
                    {intl.formatMessage({ id: 'familiar-places' })}
                  </Typography>
                </Grid>
                <Grid>
                  {formattedFamiliarPlaces.length > 0 ? (
                    <Grid
                      container
                      spacing={1}
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'flex-start'
                      }}
                    >
                      {formattedFamiliarPlaces.map(familiarPlace => (
                        <Grid key={familiarPlace}>
                          <AdviserInfoTagChip color={borderColor} label={familiarPlace} isIntoView={isIntoView} />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography color="textSecondary">-</Typography>
                  )}
                </Grid>
              </Grid>
            </Grid>

            <Grid style={FullWidthStyle}>
              <Grid container spacing={1} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <Grid>
                  <Typography
                    className="emoji"
                    sx={{
                      display: 'inline'
                    }}
                  >
                    🎓
                  </Typography>
                  <Typography
                    className="title"
                    sx={{
                      display: 'inline'
                    }}
                  >
                    {intl.formatMessage({ id: 'school' })}
                  </Typography>
                </Grid>
                <Grid>
                  {schools.length > 0 ? (
                    <Grid
                      container
                      spacing={1}
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'flex-start'
                      }}
                    >
                      {schools.map(school => (
                        <Grid key={school}>
                          <AdviserInfoTagChip color={borderColor} label={school} isIntoView={isIntoView} />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography color="textSecondary">-</Typography>
                  )}
                </Grid>
              </Grid>
            </Grid>

            <Grid style={FullWidthStyle}>
              <Grid container spacing={1} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <Grid>
                  <Typography
                    className="emoji"
                    sx={{
                      display: 'inline'
                    }}
                  >
                    📣
                  </Typography>
                  <Typography
                    className="title"
                    sx={{
                      display: 'inline'
                    }}
                  >
                    {intl.formatMessage({ id: 'main-lang' })}
                  </Typography>
                </Grid>
                <Grid>
                  {formattedMainLanguages.length > 0 ? (
                    <Grid
                      container
                      spacing={1}
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'flex-start'
                      }}
                    >
                      {formattedMainLanguages.map(tag => (
                        <Grid key={tag}>
                          <AdviserInfoTagChip
                            color={borderColor}
                            label={tryTranslate(intl, getMainLanguageIntl(tag))}
                            isIntoView={isIntoView}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography color="textSecondary">-</Typography>
                  )}
                </Grid>
              </Grid>
            </Grid>

            {otherHashtags && (
              <Grid style={FullWidthStyle}>
                <Typography
                  className="emoji"
                  sx={{
                    display: 'inline'
                  }}
                >
                  #️⃣
                </Typography>
                <Typography
                  sx={{
                    display: 'inline'
                  }}
                >
                  {tagsToTagsString(otherHashtags)}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </AdviserInfoCardAbstract>
  );
};

export default injectIntl(AdviserInfoPageSocial);
